// Runtime validation for the JSON demo inputs.
//
// This module walks the parsed JSON tree, accumulates errors into a
// list of ValidationMessage, and returns a strongly-typed DemoFile on
// success. It is the single source of truth for what makes a demo
// input valid.
//
// Design notes:
//   - Keep validation pure and decoupled from the calculation engine
//     (CLAUDE.md / project rule).
//   - Accumulate ALL errors so the user fixes everything in one pass
//     instead of whack-a-mole.
//   - Use the same enum-literal-union types that the engine uses so
//     we cannot drift apart silently.

import type {
  AffordableHousingType,
  ApplicablePriceType,
  BuildingType,
  CalculationType,
  CommercialFloor,
  IndustryProjectType,
  MainIndustrialLandType,
  ProjectType,
  PropertyCondition,
  SupplyMethod,
  UndergroundType,
} from "../types/enums.js";
import type { BuildingItem } from "../types/building-item.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";
import type { ValidationMessage } from "../types/validation.js";

import type {
  DemoBatchFile,
  DemoCapacityIncreaseFile,
  DemoChangePremiumFile,
  DemoExcessGfaFile,
  DemoFile,
  DemoLeaseRentFile,
  DemoNewSupplyFile,
  DemoTemporaryUseFile,
} from "./input.js";

// ---------------------------------------------------------------------------
// Enum value sets — declared as `as const` tuples so the literal-union
// types in src/types/enums.ts stay the source of truth at the type level
// while we still have a runtime Set for fast membership checks. If a new
// literal is added to the type, TypeScript will surface the mismatch here
// at the assignment site (`<E>(set: Set<E>)` requires the literal to be in
// the union).
// ---------------------------------------------------------------------------

const APPLICABLE_PRICE_TYPES = [
  "住宅",
  "商业",
  "办公",
  "工业",
  "新型产业",
  "酒店",
  "公共管理与服务设施",
  "公用设施",
] as const satisfies readonly ApplicablePriceType[];

const SUPPLY_METHODS = [
  "招拍挂",
  "协议",
  "租赁",
  "作价出资",
  "划拨转有偿",
  "其他",
] as const satisfies readonly SupplyMethod[];

const CALCULATION_TYPES = [
  "新供应土地",
  "变更补缴",
  "竣工超面积",
  "产业用地提高容积",
  "租赁用地年租金",
  "临时用地使用费",
  "留用土地",
  "城市更新",
  "特殊情形",
] as const satisfies readonly CalculationType[];

const PROJECT_TYPES = [
  "普通项目",
  "产业项目",
  "重点产业项目",
  "总部项目",
  "棚户区改造",
  "留用土地",
  "城市更新",
  "历史遗留",
  "其他",
] as const satisfies readonly ProjectType[];

const BUILDING_TYPES = [
  "厂房",
  "仓库",
  "研发用房",
  "物流建筑",
  "住宅",
  "宿舍",
  "办公",
  "商务公寓",
  "会议中心",
  "社会停车场库",
  "邮政网点",
  "商业",
  "加油站",
  "游乐设施",
  "加气站",
  "肉菜市场",
  "旅馆业建筑",
  "文化设施",
  "医疗卫生设施",
  "教育设施",
  "体育设施",
  "幼儿园托儿所",
  "文化遗产",
  "宗教建筑",
  "社会福利设施",
  "特殊建筑",
  "科研设施",
  "公用设施",
  "市政设施",
  "施工配套设施",
  "训考场",
  "殡葬设施",
  "交通设施",
  "综合管廊",
] as const satisfies readonly BuildingType[];

const PROPERTY_CONDITIONS = [
  "不得转让",
  "限整体转让",
  "允许分割转让",
] as const satisfies readonly PropertyCondition[];

const COMMERCIAL_FLOORS = [
  "首层商业",
  "第二层商业",
  "第三层商业",
  "第四层及以上商业",
] as const satisfies readonly CommercialFloor[];

const UNDERGROUND_TYPES = [
  "交通设施或综合管廊",
  "其他地下空间",
] as const satisfies readonly UndergroundType[];

const AFFORDABLE_HOUSING_TYPES = [
  "公共租赁住房",
  "保障性租赁住房-政府组织配租",
  "保障性租赁住房-社会主体出租",
  "配售型保障性住房",
] as const satisfies readonly AffordableHousingType[];

const INDUSTRY_PROJECT_TYPES = [
  "重点产业项目-联合申请",
  "重点产业项目-单一意向单位",
  "一般产业项目",
  "总部项目",
] as const satisfies readonly IndustryProjectType[];

const MAIN_INDUSTRIAL_LAND_TYPES = [
  "M1",
  "M0",
  "W1",
  "W0",
] as const satisfies readonly MainIndustrialLandType[];

/** True when there is at least one error- or warning-level message. */
function hasBlockingErrors(errors: ValidationMessage[]): boolean {
  return errors.some((e) => e.level === "error" || e.level === "warning");
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ValidateResult<T> {
  readonly value: T | null;
  readonly errors: ValidationMessage[];
}

/**
 * Per-item validation result inside a batch. `value` is null when the
 * item failed validation; `errors` always lists every issue found for
 * that item (never empty when `value` is null).
 */
export interface BatchItemResult {
  readonly value: DemoNewSupplyFile | DemoChangePremiumFile | null;
  readonly errors: ValidationMessage[];
}

/**
 * Validation result for a batch demo input. `value` is the parsed
 * `DemoBatchFile` (always returned once the top-level shape is valid);
 * `itemResults` is in lockstep with `value.items` and lets callers
 * distinguish per-case success vs. failure.
 */
export interface BatchValidateResult {
  readonly value: DemoBatchFile | null;
  readonly errors: ValidationMessage[];
  readonly itemResults: BatchItemResult[];
}

/**
 * Validate a parsed JSON object as a demo input file.
 *
 * - `new-supply` / `change-premium`: returns `{ value, errors }`.
 * - `batch`: returns `{ value, errors, itemResults }`; per-item errors
 *   are NOT collapsed into the top-level `errors`, they are surfaced
 *   individually via `itemResults[i].errors`.
 * - Anything else: returns `{ value: null, errors }` with a
 *   `calculationType` error message.
 *
 * The return type is a union so callers can narrow on the
 * `calculationType` field of `value` (or on the presence of
 * `itemResults`) to read the batch-only field.
 */
export function validateDemoFile(
  raw: unknown,
): ValidateResult<DemoFile> | BatchValidateResult {
  if (!isObject(raw)) {
    return {
      value: null,
      errors: [
        {
          level: "error",
          field: "<root>",
          message: "顶层必须是 JSON 对象。",
        },
      ],
    };
  }

  const ct = raw["calculationType"];
  if (ct === "new-supply") {
    return validateNewSupply(raw);
  }
  if (ct === "change-premium") {
    return validateChangePremium(raw);
  }
  if (ct === "excess-gfa") {
    return validateExcessGfa(raw);
  }
  if (ct === "temporary-use" || ct === "short-term-lease") {
    return validateTemporaryUse(raw);
  }
  if (ct === "lease-rent") {
    return validateLeaseRent(raw);
  }
  if (ct === "capacity-increase") {
    return validateCapacityIncrease(raw);
  }
  if (ct === "batch") {
    return validateBatch(raw);
  }
  return {
    value: null,
    errors: [
      {
        level: "error",
        field: "calculationType",
        message: `calculationType 必须是 "new-supply" / "change-premium" / "excess-gfa" / "temporary-use" / "short-term-lease" / "lease-rent" / "capacity-increase" / "batch"，收到 ${stringify(ct)}。`,
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// new-supply / change-premium
// ---------------------------------------------------------------------------

function validateNewSupply(raw: Record<string, unknown>): ValidateResult<DemoNewSupplyFile> {
  const errors: ValidationMessage[] = [];
  const input = raw["input"];
  if (!isObject(input)) {
    errors.push({
      level: "error",
      field: "input",
      message: "new-supply 必须包含 input 对象。",
    });
    return { value: null, errors };
  }
  const parcel = validateParcel(input["parcel"], "input.parcel", errors);
  const prices = validateMarketPrices(input["prices"], "input.prices", errors);
  const items = validateItems(input["items"], "input.items", errors);
  if (parcel && prices && items) {
    const value: DemoNewSupplyFile = {
      calculationType: "new-supply",
      input: { parcel, prices, items },
    };
    return { value, errors };
  }
  return { value: null, errors };
}

function validateChangePremium(
  raw: Record<string, unknown>,
): ValidateResult<DemoChangePremiumFile> {
  const errors: ValidationMessage[] = [];
  const input = raw["input"];
  if (!isObject(input)) {
    errors.push({
      level: "error",
      field: "input",
      message: "change-premium 必须包含 input 对象。",
    });
    return { value: null, errors };
  }
  const parcelBefore = validateParcel(
    input["parcelBefore"],
    "input.parcelBefore",
    errors,
  );
  const parcelAfter = validateParcel(
    input["parcelAfter"],
    "input.parcelAfter",
    errors,
  );
  const prices = validateMarketPrices(input["prices"], "input.prices", errors);
  const itemsBefore = validateItems(
    input["itemsBefore"],
    "input.itemsBefore",
    errors,
  );
  const itemsAfter = validateItems(
    input["itemsAfter"],
    "input.itemsAfter",
    errors,
  );
  if (parcelBefore && parcelAfter && prices && itemsBefore && itemsAfter) {
    const value: DemoChangePremiumFile = {
      calculationType: "change-premium",
      input: { parcelBefore, parcelAfter, prices, itemsBefore, itemsAfter },
    };
    return { value, errors };
  }
  return { value: null, errors };
}

// ---------------------------------------------------------------------------
// excess-gfa / temporary-use / short-term-lease / lease-rent /
// capacity-increase
// ---------------------------------------------------------------------------

function validateExcessGfa(
  raw: Record<string, unknown>,
): ValidateResult<DemoExcessGfaFile> {
  const errors: ValidationMessage[] = [];
  const input = raw["input"];
  if (!isObject(input)) {
    errors.push({
      level: "error",
      field: "input",
      message: "excess-gfa 必须包含 input 对象。",
    });
    return { value: null, errors };
  }
  const parcelBefore = validateParcel(
    input["parcelBefore"],
    "input.parcelBefore",
    errors,
  );
  const parcelAfter = validateParcel(
    input["parcelAfter"],
    "input.parcelAfter",
    errors,
  );
  const prices = validateMarketPrices(input["prices"], "input.prices", errors);
  const itemsBefore = validateItems(
    input["itemsBefore"],
    "input.itemsBefore",
    errors,
  );
  const itemsAfter = validateItems(
    input["itemsAfter"],
    "input.itemsAfter",
    errors,
  );

  // planningConfirmed is required.
  const pc = input["planningConfirmed"];
  if (typeof pc !== "boolean") {
    errors.push({
      level: "error",
      field: "input.planningConfirmed",
      message: `input.planningConfirmed 必须是布尔值，收到 ${stringify(pc)}。`,
    });
  }

  if (
    parcelBefore &&
    parcelAfter &&
    prices &&
    itemsBefore &&
    itemsAfter &&
    typeof pc === "boolean"
  ) {
    const value: DemoExcessGfaFile = {
      calculationType: "excess-gfa",
      input: {
        parcelBefore,
        parcelAfter,
        prices,
        itemsBefore,
        itemsAfter,
        planningConfirmed: pc,
      },
    };
    return { value, errors };
  }
  return { value: null, errors };
}

function validateTemporaryUse(
  raw: Record<string, unknown>,
): ValidateResult<DemoTemporaryUseFile> {
  const errors: ValidationMessage[] = [];
  const input = raw["input"];
  if (!isObject(input)) {
    errors.push({
      level: "error",
      field: "input",
      message: "temporary-use / short-term-lease 必须包含 input 对象。",
    });
    return { value: null, errors };
  }
  const parcel = validateParcel(input["parcel"], "input.parcel", errors);
  const prices = validateMarketPrices(input["prices"], "input.prices", errors);
  const items = validateItems(input["items"], "input.items", errors);

  // landTermYears: positive finite number > 0.
  const lty = input["landTermYears"];
  if (!isPositiveFiniteNumber(lty)) {
    errors.push({
      level: "error",
      field: "input.landTermYears",
      message: `input.landTermYears 必须是大于 0 的有限数字，收到 ${stringify(lty)}。`,
    });
  }

  // temporaryUserType: required enum.
  const tut = input["temporaryUserType"];
  if (tut !== "普通" && tut !== "政府或政府有关部门") {
    errors.push({
      level: "error",
      field: "input.temporaryUserType",
      message:
        `input.temporaryUserType 必须是 "普通" / "政府或政府有关部门"，收到 ${stringify(tut)}。`,
    });
  }

  if (parcel && prices && items && isPositiveFiniteNumber(lty) &&
      (tut === "普通" || tut === "政府或政府有关部门")) {
    const value: DemoTemporaryUseFile = {
      calculationType:
        raw["calculationType"] === "short-term-lease"
          ? "short-term-lease"
          : "temporary-use",
      input: {
        parcel,
        prices,
        items,
        landTermYears: lty as number,
        temporaryUserType: tut as "普通" | "政府或政府有关部门",
      },
    };
    return { value, errors };
  }
  return { value: null, errors };
}

function validateLeaseRent(
  raw: Record<string, unknown>,
): ValidateResult<DemoLeaseRentFile> {
  const errors: ValidationMessage[] = [];
  const input = raw["input"];
  if (!isObject(input)) {
    errors.push({
      level: "error",
      field: "input",
      message: "lease-rent 必须包含 input 对象。",
    });
    return { value: null, errors };
  }
  const parcel = validateParcel(input["parcel"], "input.parcel", errors);
  const prices = validateMarketPrices(input["prices"], "input.prices", errors);
  const items = validateItems(input["items"], "input.items", errors);
  if (parcel && prices && items) {
    const value: DemoLeaseRentFile = {
      calculationType: "lease-rent",
      input: { parcel, prices, items },
    };
    return { value, errors };
  }
  return { value: null, errors };
}

function validateCapacityIncrease(
  raw: Record<string, unknown>,
): ValidateResult<DemoCapacityIncreaseFile> {
  const errors: ValidationMessage[] = [];
  const input = raw["input"];
  if (!isObject(input)) {
    errors.push({
      level: "error",
      field: "input",
      message: "capacity-increase 必须包含 input 对象。",
    });
    return { value: null, errors };
  }
  const parcelBefore = validateParcel(
    input["parcelBefore"],
    "input.parcelBefore",
    errors,
  );
  const parcelAfter = validateParcel(
    input["parcelAfter"],
    "input.parcelAfter",
    errors,
  );
  const prices = validateMarketPrices(input["prices"], "input.prices", errors);
  const itemsBefore = validateItems(
    input["itemsBefore"],
    "input.itemsBefore",
    errors,
  );
  const itemsAfter = validateItems(
    input["itemsAfter"],
    "input.itemsAfter",
    errors,
  );

  // mainIndustrialLandType: required for capacity-increase.
  const milt = input["mainIndustrialLandType"];
  if (milt !== "M1" && milt !== "M0" && milt !== "W1" && milt !== "W0") {
    errors.push({
      level: "error",
      field: "input.mainIndustrialLandType",
      message:
        `input.mainIndustrialLandType 必须是 "M1" / "M0" / "W1" / "W0"，收到 ${stringify(milt)}。`,
    });
  }

  if (
    parcelBefore &&
    parcelAfter &&
    prices &&
    itemsBefore &&
    itemsAfter &&
    (milt === "M1" || milt === "M0" || milt === "W1" || milt === "W0")
  ) {
    const value: DemoCapacityIncreaseFile = {
      calculationType: "capacity-increase",
      input: {
        parcelBefore,
        parcelAfter,
        prices,
        itemsBefore,
        itemsAfter,
        mainIndustrialLandType: milt as "M1" | "M0" | "W1" | "W0",
      },
    };
    return { value, errors };
  }
  return { value: null, errors };
}

// ---------------------------------------------------------------------------
// batch
// ---------------------------------------------------------------------------

function validateBatch(raw: Record<string, unknown>): BatchValidateResult {
  const errors: ValidationMessage[] = [];
  const itemsRaw = raw["items"];
  if (!Array.isArray(itemsRaw)) {
    errors.push({
      level: "error",
      field: "items",
      message: `batch 必须包含 items 数组，收到 ${stringify(itemsRaw)}。`,
    });
    return { value: null, errors, itemResults: [] };
  }
  if (itemsRaw.length === 0) {
    errors.push({
      level: "error",
      field: "items",
      message: "batch.items 不能为空。",
    });
    return { value: null, errors, itemResults: [] };
  }

  const itemResults: BatchItemResult[] = [];
  const validItems: DemoBatchFile["items"] = [];

  itemsRaw.forEach((item, idx) => {
    if (!isObject(item)) {
      errors.push({
        level: "error",
        field: `items[${idx}]`,
        message: `items[${idx}] 必须是对象。`,
      });
      itemResults.push({ value: null, errors: [] });
      return;
    }

    const title = item["title"];
    if (title !== undefined && typeof title !== "string") {
      errors.push({
        level: "error",
        field: `items[${idx}].title`,
        message: `items[${idx}].title 必须是字符串。`,
      });
    }

    const innerErrors: ValidationMessage[] = [];
    const inner = validateBatchItemBody(item, idx, innerErrors);
    itemResults.push({ value: inner, errors: innerErrors });
    if (inner) {
      validItems.push({
        title: typeof title === "string" ? title : undefined,
        file: inner,
      });
    }
  });

  // Top-level errors include any structural problems; per-item errors
  // live in `itemResults`. The returned batch value is always built
  // from the items that did validate (so the runner can still report
  // partial success).
  const value: DemoBatchFile = {
    calculationType: "batch",
    items: validItems,
  };
  return { value, errors, itemResults };
}

function validateBatchItemBody(
  item: Record<string, unknown>,
  idx: number,
  sink: ValidationMessage[],
): DemoNewSupplyFile | DemoChangePremiumFile | null {
  // Each batch item must have its own `calculationType` (plus the rest
  // of the new-supply / change-premium shape).
  const ct = item["calculationType"];
  if (ct === "new-supply") {
    const inner = validateNewSupply(item);
    // Re-tag fields so consumers can tell which batch item an error
    // originated from.
    pushPrefixedErrors(inner.errors, `items[${idx}]`, sink);
    return inner.value;
  }
  if (ct === "change-premium") {
    const inner = validateChangePremium(item);
    pushPrefixedErrors(inner.errors, `items[${idx}]`, sink);
    return inner.value;
  }
  if (ct === undefined || ct === null) {
    sink.push({
      level: "error",
      field: `items[${idx}].calculationType`,
      message: `items[${idx}].calculationType 必填，期望 "new-supply" / "change-premium"。`,
    });
    return null;
  }
  sink.push({
    level: "error",
    field: `items[${idx}].calculationType`,
    message: `items[${idx}].calculationType 取值无效，收到 ${stringify(ct)}。`,
  });
  return null;
}

function pushPrefixedErrors(
  errors: ValidationMessage[],
  prefix: string,
  sink: ValidationMessage[],
): void {
  for (const e of errors) {
    sink.push({
      ...e,
      field: e.field ? `${prefix}.${e.field}` : prefix,
    });
  }
}

// ---------------------------------------------------------------------------
// Parcel
// ---------------------------------------------------------------------------

function validateParcel(
  raw: unknown,
  fieldPath: string,
  errors: ValidationMessage[],
): Parcel | null {
  if (!isObject(raw)) {
    errors.push({
      level: "error",
      field: fieldPath,
      message: `${fieldPath} 必须是对象。`,
    });
    return null;
  }

  const requiredStrings: Array<keyof Parcel & string> = [
    "projectName",
    "calculationDate",
  ];
  for (const key of requiredStrings) {
    const value = raw[key];
    if (typeof value !== "string" || value.trim() === "") {
      errors.push({
        level: "error",
        field: `${fieldPath}.${key}`,
        message: `${fieldPath}.${key} 必须是非空字符串。`,
        suggestion: "请填写项目名称 / 测算时点。",
      });
    }
  }

  const positiveNumbers: Array<keyof Parcel & string> = ["landArea", "totalGfa"];
  for (const key of positiveNumbers) {
    const v = raw[key];
    if (!isPositiveFiniteNumber(v)) {
      errors.push({
        level: "error",
        field: `${fieldPath}.${key}`,
        message: `${fieldPath}.${key} 必须是大于 0 的有限数字，收到 ${stringify(v)}。`,
      });
    }
  }

  // governmentOwnedGfa is optional but, if present, must be a non-negative number.
  const govGfa = raw["governmentOwnedGfa"];
  if (govGfa !== undefined && !isNonNegativeFiniteNumber(govGfa)) {
    errors.push({
      level: "error",
      field: `${fieldPath}.governmentOwnedGfa`,
      message: `${fieldPath}.governmentOwnedGfa 必须是 ≥ 0 的有限数字，收到 ${stringify(govGfa)}。`,
    });
  }

  requireEnum(raw, "supplyMethod", SUPPLY_METHODS, fieldPath, errors);
  requireEnum(
    raw,
    "calculationType",
    CALCULATION_TYPES,
    fieldPath,
    errors,
  );
  requireEnum(raw, "mainLandUse", APPLICABLE_PRICE_TYPES, fieldPath, errors);
  requireEnum(raw, "projectType", PROJECT_TYPES, fieldPath, errors);

  // mainIndustrialLandType is optional (only required for capacity-
  // increase calculations); when present, must be one of M1/M0/W1/W0.
  requireOptionalEnum(
    raw,
    "mainIndustrialLandType",
    MAIN_INDUSTRIAL_LAND_TYPES,
    fieldPath,
    errors,
  );

  // parcelId is optional; if present, must be a string.
  if (raw["parcelId"] !== undefined && typeof raw["parcelId"] !== "string") {
    errors.push({
      level: "error",
      field: `${fieldPath}.parcelId`,
      message: `${fieldPath}.parcelId 必须是字符串。`,
    });
  }
  if (raw["remark"] !== undefined && typeof raw["remark"] !== "string") {
    errors.push({
      level: "error",
      field: `${fieldPath}.remark`,
      message: `${fieldPath}.remark 必须是字符串。`,
    });
  }

  return !hasBlockingErrors(errors) ? (raw as unknown as Parcel) : null;
}

// ---------------------------------------------------------------------------
// MarketPriceSet
// ---------------------------------------------------------------------------

const PRICE_KEYS: ReadonlyArray<keyof MarketPriceSet & string> = [
  "residential",
  "commercial",
  "office",
  "industrial",
  "m0",
  "hotel",
  "publicService",
  "utility",
];

function validateMarketPrices(
  raw: unknown,
  fieldPath: string,
  errors: ValidationMessage[],
): MarketPriceSet | null {
  if (!isObject(raw)) {
    errors.push({
      level: "error",
      field: fieldPath,
      message: `${fieldPath} 必须是对象。`,
    });
    return null;
  }

  for (const key of PRICE_KEYS) {
    const v = raw[key];
    if (v === undefined) continue;
    if (!isNonNegativeFiniteNumber(v)) {
      errors.push({
        level: "error",
        field: `${fieldPath}.${key}`,
        message: `${fieldPath}.${key} 必须是 ≥ 0 的有限数字，收到 ${stringify(v)}。`,
      });
    }
  }

  if (
    raw["priceSource"] !== undefined &&
    typeof raw["priceSource"] !== "string"
  ) {
    errors.push({
      level: "error",
      field: `${fieldPath}.priceSource`,
      message: `${fieldPath}.priceSource 必须是字符串。`,
    });
  }
  if (raw["priceDate"] !== undefined && typeof raw["priceDate"] !== "string") {
    errors.push({
      level: "error",
      field: `${fieldPath}.priceDate`,
      message: `${fieldPath}.priceDate 必须是字符串。`,
    });
  }

  // Official-source metadata (all optional). Each present field must
  // be the correct type. `marketPriceYear` is additionally constrained
  // to look like a 4-digit year; date fields are validated as
  // ISO 8601 strings with a soft info note on mismatch (so users
  // can still record irregular formats).
  validateMarketPriceMetadata(raw, fieldPath, errors);

  return !hasBlockingErrors(errors) ? (raw as unknown as MarketPriceSet) : null;
}

const METADATA_STRING_KEYS: ReadonlyArray<keyof MarketPriceSet & string> = [
  "marketPriceSource",
  "marketPriceYear",
  "valuationDate",
  "effectiveDate",
  "standardParcelCode",
  "benchmarkZoneCode",
  "officialQueryUrl",
  "priceSourceRemark",
];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function validateMarketPriceMetadata(
  raw: Record<string, unknown>,
  fieldPath: string,
  errors: ValidationMessage[],
): void {
  for (const key of METADATA_STRING_KEYS) {
    const v = raw[key];
    if (v === undefined) continue;
    if (typeof v !== "string") {
      errors.push({
        level: "error",
        field: `${fieldPath}.${key}`,
        message: `${fieldPath}.${key} 必须是字符串，收到 ${stringify(v)}。`,
      });
      continue;
    }
    if (v === "") {
      errors.push({
        level: "error",
        field: `${fieldPath}.${key}`,
        message: `${fieldPath}.${key} 是空字符串。请填写有效值或删除该字段。`,
      });
    }
  }
  // marketPriceYear: 4-digit year
  const year = raw["marketPriceYear"];
  if (typeof year === "string" && year !== "" && !/^\d{4}$/.test(year)) {
    errors.push({
      level: "error",
      field: `${fieldPath}.marketPriceYear`,
      message: `${fieldPath}.marketPriceYear 必须是 4 位年份字符串（如 "2026"），收到 ${stringify(year)}。`,
    });
  }
  // ISO 8601 date fields (info-level when the format is unusual)
  for (const key of ["valuationDate", "effectiveDate"] as const) {
    const v = raw[key];
    if (typeof v === "string" && v !== "" && !ISO_DATE_RE.test(v)) {
      errors.push({
        level: "info",
        field: `${fieldPath}.${key}`,
        message: `${fieldPath}.${key} 不是 ISO 8601 (YYYY-MM-DD) 格式：${stringify(v)}。`,
        suggestion: "建议使用 YYYY-MM-DD 格式以便跨工具解析。",
      });
    }
  }
}

// ---------------------------------------------------------------------------
// BuildingItem
// ---------------------------------------------------------------------------

function validateItems(
  raw: unknown,
  fieldPath: string,
  errors: ValidationMessage[],
): BuildingItem[] | null {
  if (!Array.isArray(raw)) {
    errors.push({
      level: "error",
      field: fieldPath,
      message: `${fieldPath} 必须是数组，收到 ${stringify(raw)}。`,
    });
    return null;
  }

  let ok = true;
  raw.forEach((item, idx) => {
    if (!validateItem(item, `${fieldPath}[${idx}]`, errors)) ok = false;
  });
  return ok ? (raw as unknown as BuildingItem[]) : null;
}

function validateItem(
  raw: unknown,
  fieldPath: string,
  errors: ValidationMessage[],
): BuildingItem | null {
  if (!isObject(raw)) {
    errors.push({
      level: "error",
      field: fieldPath,
      message: `${fieldPath} 必须是对象。`,
    });
    return null;
  }

  if (typeof raw["id"] !== "string" || (raw["id"] as string).trim() === "") {
    errors.push({
      level: "error",
      field: `${fieldPath}.id`,
      message: `${fieldPath}.id 必须是非空字符串。`,
    });
  }

  if (!isPositiveFiniteNumber(raw["gfa"])) {
    errors.push({
      level: "error",
      field: `${fieldPath}.gfa`,
      message: `${fieldPath}.gfa 必须是大于 0 的有限数字，收到 ${stringify(raw["gfa"])}。`,
    });
  }

  if (!isPositiveFiniteNumber(raw["landTermYears"])) {
    errors.push({
      level: "error",
      field: `${fieldPath}.landTermYears`,
      message: `${fieldPath}.landTermYears 必须是大于 0 的有限数字，收到 ${stringify(raw["landTermYears"])}。`,
    });
  }

  requireEnum(raw, "buildingType", BUILDING_TYPES, fieldPath, errors);
  requireEnum(raw, "propertyCondition", PROPERTY_CONDITIONS, fieldPath, errors);

  if (typeof raw["isGovernmentOwned"] !== "boolean") {
    errors.push({
      level: "error",
      field: `${fieldPath}.isGovernmentOwned`,
      message: `${fieldPath}.isGovernmentOwned 必须是布尔值，收到 ${stringify(raw["isGovernmentOwned"])}。`,
    });
  }
  if (typeof raw["isUnderground"] !== "boolean") {
    errors.push({
      level: "error",
      field: `${fieldPath}.isUnderground`,
      message: `${fieldPath}.isUnderground 必须是布尔值，收到 ${stringify(raw["isUnderground"])}。`,
    });
  }

  requireOptionalEnum(
    raw,
    "commercialFloor",
    COMMERCIAL_FLOORS,
    fieldPath,
    errors,
  );
  requireOptionalEnum(
    raw,
    "undergroundType",
    UNDERGROUND_TYPES,
    fieldPath,
    errors,
  );
  requireOptionalEnum(
    raw,
    "affordableHousingType",
    AFFORDABLE_HOUSING_TYPES,
    fieldPath,
    errors,
  );
  requireOptionalEnum(
    raw,
    "industryProjectType",
    INDUSTRY_PROJECT_TYPES,
    fieldPath,
    errors,
  );
  requireOptionalEnum(
    raw,
    "applicablePriceType",
    APPLICABLE_PRICE_TYPES,
    fieldPath,
    errors,
  );

  if (
    raw["isAffordableHousing"] !== undefined &&
    typeof raw["isAffordableHousing"] !== "boolean"
  ) {
    errors.push({
      level: "error",
      field: `${fieldPath}.isAffordableHousing`,
      message: `${fieldPath}.isAffordableHousing 必须是布尔值。`,
    });
  }
  if (
    raw["isIndustryOriented"] !== undefined &&
    typeof raw["isIndustryOriented"] !== "boolean"
  ) {
    errors.push({
      level: "error",
      field: `${fieldPath}.isIndustryOriented`,
      message: `${fieldPath}.isIndustryOriented 必须是布尔值。`,
    });
  }
  if (
    raw["isSupportingFacility"] !== undefined &&
    typeof raw["isSupportingFacility"] !== "boolean"
  ) {
    errors.push({
      level: "error",
      field: `${fieldPath}.isSupportingFacility`,
      message: `${fieldPath}.isSupportingFacility 必须是布尔值。`,
    });
  }
  if (raw["remark"] !== undefined && typeof raw["remark"] !== "string") {
    errors.push({
      level: "error",
      field: `${fieldPath}.remark`,
      message: `${fieldPath}.remark 必须是字符串。`,
    });
  }

  return !hasBlockingErrors(errors) ? (raw as unknown as BuildingItem) : null;
}

// ---------------------------------------------------------------------------
// Generic helpers
// ---------------------------------------------------------------------------

function requireEnum<E extends string>(
  raw: Record<string, unknown>,
  key: string,
  values: readonly E[],
  fieldPath: string,
  errors: ValidationMessage[],
): void {
  const v = raw[key];
  if (v === undefined) {
    errors.push({
      level: "error",
      field: `${fieldPath}.${key}`,
      message: `${fieldPath}.${key} 必填，期望 ${formatAllowed(values)}。`,
    });
    return;
  }
  if (!values.includes(v as E)) {
    errors.push({
      level: "error",
      field: `${fieldPath}.${key}`,
      message:
        `${fieldPath}.${key} 取值无效，收到 ${stringify(v)}，` +
        `期望 ${formatAllowed(values)}。`,
    });
  }
}

function requireOptionalEnum<E extends string>(
  raw: Record<string, unknown>,
  key: string,
  values: readonly E[],
  fieldPath: string,
  errors: ValidationMessage[],
): void {
  const v = raw[key];
  if (v === undefined) return;
  if (!values.includes(v as E)) {
    errors.push({
      level: "error",
      field: `${fieldPath}.${key}`,
      message:
        `${fieldPath}.${key} 取值无效，收到 ${stringify(v)}，` +
        `期望 ${formatAllowed(values)}。`,
    });
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isPositiveFiniteNumber(v: unknown): boolean {
  return typeof v === "number" && Number.isFinite(v) && v > 0;
}

function isNonNegativeFiniteNumber(v: unknown): boolean {
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
}

function stringify(v: unknown): string {
  if (typeof v === "string") return JSON.stringify(v);
  if (v === undefined) return "undefined";
  if (v === null) return "null";
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function formatAllowed(values: readonly string[]): string {
  return values.map((v) => JSON.stringify(v)).join(" / ");
}
